<template>
  <div class="w-full p-4">
    <div ref="topStickyRef" class="sticky top-14 z-30 bg-gray-200 -mx-4 px-4 pt-3 pb-1">
    <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-4">
      <h2 class="text-lg font-semibold">Team Expenses</h2>
      <div class="text-sm text-gray-600">
        {{ team.length }} direct report<span v-if="team.length !== 1">s</span>
      </div>
    </div>
    <template v-if="team.length > 0 || isApproverOverride">
      <!-- Tabs -->
      <div class="flex flex-row flex-wrap gap-1 mb-3 text-sm border-b border-gray-300">
        <button
          v-if="team.length > 0"
          class="px-3 py-1.5 -mb-px border-b-2"
          :class="activeTab === 'pending'
            ? 'border-accent-primary text-accent-primary font-semibold'
            : 'border-transparent text-gray-500 hover:text-dark-primary'"
          @click="activeTab = 'pending'"
        >
          Pending My Approval
          <span class="ml-1 text-xs text-gray-400">{{ pendingCount }}</span>
        </button>
        <button
          v-if="isApproverOverride"
          class="px-3 py-1.5 -mb-px border-b-2"
          :class="activeTab === 'all_pending'
            ? 'border-accent-primary text-accent-primary font-semibold'
            : 'border-transparent text-gray-500 hover:text-dark-primary'"
          @click="activeTab = 'all_pending'"
        >
          All Pending (any team)
          <span class="ml-1 text-xs text-gray-400">{{ allPendingCount }}</span>
        </button>
        <button
          v-if="team.length > 0"
          class="px-3 py-1.5 -mb-px border-b-2"
          :class="activeTab === 'history'
            ? 'border-accent-primary text-accent-primary font-semibold'
            : 'border-transparent text-gray-500 hover:text-dark-primary'"
          @click="activeTab = 'history'"
        >
          Team History
        </button>
      </div>

      <!-- Bulk action bar (pending tabs, when rows selected) -->
      <div
        v-if="(activeTab === 'pending' || activeTab === 'all_pending') && selectedIds.length > 0"
        class="flex flex-wrap items-center gap-2 mb-3 p-2 rounded bg-gray-100 border border-gray-300 text-sm"
      >
        <span>{{ selectedIds.length }} selected</span>
        <button
          class="px-2 py-1 rounded bg-accent-primary text-white hover:bg-accent-secondary disabled:opacity-50"
          :disabled="bulkWorking"
          @click="bulkApprove"
        >{{ bulkWorking ? 'Working...' : 'Approve Selected' }}</button>
        <button
          class="px-2 py-1 rounded border border-red-500 text-red-700 hover:bg-red-50 disabled:opacity-50"
          :disabled="bulkWorking"
          @click="rejectModalOpen = true"
        >Reject Selected</button>
        <button
          class="ml-auto text-xs text-gray-500 hover:underline"
          @click="selectedIds = []"
        >Clear</button>
      </div>

      <div v-if="bulkErrors.length > 0" class="mb-3 p-2 rounded bg-red-50 border border-red-200 text-sm text-red-700">
        Some records could not be processed:
        <ul class="list-disc ml-5 mt-1">
          <li v-for="e in bulkErrors" :key="e.id">#{{ e.id }}: {{ e.detail }}</li>
        </ul>
      </div>

      <!-- Filters (visible on every tab) -->
      <div class="flex flex-wrap gap-3 mb-3 items-end text-sm">
        <div class="flex-1 min-w-[10rem]">
          <label class="block text-xs text-gray-500 mb-0.5">Search</label>
          <input
            v-model="filters.search"
            type="text"
            placeholder="Search description, submitter, client, engagement..."
            class="w-full h-9 px-2 border border-gray-400 rounded-md"
          />
        </div>
        <div>
          <label class="block text-xs text-gray-500 mb-0.5">Submitter</label>
          <select v-model="filters.submitter" class="h-9 px-2 border border-gray-400 rounded-md w-44">
            <option :value="null">All</option>
            <option v-for="u in submitterOptions" :key="u.id" :value="u.id">{{ fullName(u) }}</option>
          </select>
        </div>
        <div v-if="activeTab === 'history'">
          <label class="block text-xs text-gray-500 mb-0.5">Status</label>
          <select v-model="filters.status" class="h-9 px-2 border border-gray-400 rounded-md w-44">
            <option :value="null">All</option>
            <option value="draft">Draft</option>
            <option value="submitted">Pending Manager</option>
            <option value="manager_approved">Pending Accounting</option>
            <option value="accounting_approved">Approved</option>
            <option value="closed">Closed</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-gray-500 mb-0.5">Category</label>
          <select v-model="filters.category" class="h-9 px-2 border border-gray-400 rounded-md w-44">
            <option :value="null">All</option>
            <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-gray-500 mb-0.5">Group</label>
          <input
            v-model="filters.group_label"
            type="text"
            list="team-expenses-group-options"
            placeholder="Any"
            class="h-9 px-2 border border-gray-400 rounded-md w-40"
          />
          <datalist id="team-expenses-group-options">
            <option v-for="g in teamGroupLabelOptions" :key="g" :value="g" />
          </datalist>
        </div>
        <div v-if="canViewIsRetro">
          <label class="block text-xs text-gray-500 mb-0.5">Retro</label>
          <select v-model="filters.is_retro" class="h-9 px-2 border border-gray-400 rounded-md w-32">
            <option :value="null">All</option>
            <option :value="true">Retro only</option>
            <option :value="false">Non-retro</option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-gray-500 mb-0.5">From</label>
          <input v-model="filters.dateFrom" type="date" class="h-9 px-2 border border-gray-400 rounded-md w-36" />
        </div>
        <div>
          <label class="block text-xs text-gray-500 mb-0.5">To</label>
          <input v-model="filters.dateTo" type="date" class="h-9 px-2 border border-gray-400 rounded-md w-36" />
        </div>
        <button
          v-if="hasActiveFilters"
          class="px-2 py-1.5 text-xs border border-gray-400 rounded hover:bg-gray-100"
          @click="clearFilters"
        >Clear filters</button>
      </div>

      <!-- Filtered total -->
      <div class="text-sm text-gray-600 mb-3 text-right">
        Filtered total
        <span class="font-semibold text-dark-primary">${{ formatTotal(filteredTotal) }}</span>
        <span class="text-gray-400 mx-1">·</span>
        {{ visibleExpenses.length }} row<span v-if="visibleExpenses.length !== 1">s</span>
        <template v-if="selectedIds.length">
          <span class="text-gray-400 mx-1">·</span>
          <span class="font-semibold text-accent-primary">{{ selectedIds.length }} selected</span>
        </template>
      </div>
    </template>
    </div>

    <div v-if="team.length === 0 && !isApproverOverride" class="p-6 text-center text-gray-500">
      You have no direct reports assigned to you. If that looks wrong, an admin
      can set up the reporting relationship.
    </div>

    <template v-else>
      <!-- Mobile card list (narrow screens) -->
      <div class="md:hidden space-y-2">
        <div v-if="loading" class="p-4 text-center text-gray-500 text-sm">Loading...</div>
        <div v-else-if="visibleExpenses.length === 0" class="p-4 text-center text-gray-500 text-sm">
          <template v-if="activeTab === 'pending'">Nothing pending your approval.</template>
          <template v-else-if="activeTab === 'all_pending'">No expenses awaiting any manager's approval.</template>
          <template v-else>No expenses match your filters.</template>
        </div>
        <template v-for="exp in visibleExpenses" :key="`m-${exp.id}`">
          <div
            class="border border-gray-300 rounded-md bg-white p-3 cursor-pointer hover:bg-gray-50"
            @click="toggleExpand(exp.id)"
          >
            <div class="flex items-start gap-2">
              <div
                v-if="activeTab === 'pending' || activeTab === 'all_pending'"
                class="pt-1"
                @click.stop
              >
                <input type="checkbox" :value="exp.id" v-model="selectedIds" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-start justify-between gap-2">
                  <div class="min-w-0">
                    <div class="text-sm font-semibold">
                      {{ exp.submitter ? fullName(exp.submitter) : '—' }}
                    </div>
                    <div class="text-xs text-gray-500 mt-0.5">
                      #{{ exp.id }} · {{ formatDate(exp.date_of_expense) }}
                      · {{ exp.category ? exp.category.name : '—' }}
                    </div>
                  </div>
                  <div class="text-right whitespace-nowrap flex-shrink-0">
                    <div class="font-semibold">
                      <span
                        v-if="exp.payment_method === 'company_paid'"
                        class="mr-1 px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-700"
                        title="Paid via company card"
                      >co.</span>
                      ${{ formatAmount(exp.amount) }}
                    </div>
                  </div>
                </div>
                <div class="text-sm mt-1 break-words">{{ exp.description }}</div>
                <div v-if="exp.group_label" class="mt-1">
                  <span
                    class="inline-block px-1.5 py-0.5 text-xs rounded bg-amber-100 text-amber-800"
                    title="Group"
                  >{{ exp.group_label }}</span>
                </div>
                <div class="flex items-center flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-gray-600">
                  <div class="flex flex-col items-start gap-0.5">
                    <StatusPill :status="exp.status" />
                    <span v-if="exp.is_retro" class="px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-500">Retro</span>
                  </div>
                  <span>{{ exp.receipt_count || 0 }} receipt<span v-if="exp.receipt_count !== 1">s</span></span>
                  <span class="text-gray-400 ml-auto">{{ expandedId === exp.id ? '▼ Hide details' : '▶ View details' }}</span>
                </div>
                <div v-if="exp.is_client_reimbursable" class="mt-1 text-xs text-gray-700">
                  {{ exp.client_name || '—' }}<span v-if="exp.engagement_title"> — {{ exp.engagement_title }}</span>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="expandedId === exp.id"
            class="border border-gray-300 rounded-md bg-gray-50 p-3"
          >
            <ExpenseReviewPanel
              :expenseId="exp.id"
              :detail="detailCache[exp.id]"
              :loading="detailLoading[exp.id]"
              :allowApproval="(activeTab === 'pending' || activeTab === 'all_pending') && exp.status === 'submitted'"
              @approved="onApproved"
              @rejected="onRejected"
              @request-reload="reloadDetail(exp.id)"
            />
          </div>
        </template>
      </div>

      <!-- Desktop / tablet table -->
      <div class="hidden md:block border border-gray-400 rounded-md">
        <table class="table-auto w-full text-sm">
          <thead class="bg-dark-primary text-white sticky z-20" :style="{ top: (topStickyHeight + 56) + 'px' }">
            <tr>
              <th class="px-3 py-2 w-8">
                <input
                  v-if="activeTab === 'pending' || activeTab === 'all_pending'"
                  type="checkbox"
                  :checked="allPendingSelected"
                  @change="toggleAllPending"
                />
              </th>
              <th class="text-left px-3 py-2 w-10"></th>
              <th class="text-left px-3 py-2 w-16">ID</th>
              <th class="text-left px-3 py-2">Submitter</th>
              <th class="text-left px-3 py-2">Date</th>
              <th class="text-left px-3 py-2">Category</th>
              <th class="text-left px-3 py-2">Description</th>
              <th class="text-right px-3 py-2">Amount</th>
              <th class="text-left px-3 py-2">Status</th>
              <th class="text-left px-3 py-2">Client / Engagement</th>
              <th class="text-center px-3 py-2">Receipts</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="11" class="px-3 py-4 text-center text-gray-500">Loading...</td>
            </tr>
            <tr v-else-if="visibleExpenses.length === 0">
              <td colspan="11" class="px-3 py-4 text-center text-gray-500">
                <template v-if="activeTab === 'pending'">Nothing pending your approval.</template>
                <template v-else>No expenses match your filters.</template>
              </td>
            </tr>
            <template v-for="exp in visibleExpenses" :key="exp.id">
              <tr
                class="border-t border-gray-200 hover:bg-gray-50 cursor-pointer"
                @click="toggleExpand(exp.id)"
              >
                <td class="px-3 py-2" @click.stop>
                  <input
                    v-if="activeTab === 'pending' || activeTab === 'all_pending'"
                    type="checkbox"
                    :value="exp.id"
                    v-model="selectedIds"
                  />
                </td>
                <td class="px-3 py-2 text-gray-400">
                  {{ expandedId === exp.id ? '▼' : '▶' }}
                </td>
                <td class="px-3 py-2 whitespace-nowrap text-gray-500">#{{ exp.id }}</td>
                <td class="px-3 py-2 whitespace-nowrap">
                  {{ exp.submitter ? fullName(exp.submitter) : '—' }}
                </td>
                <td class="px-3 py-2 whitespace-nowrap">{{ formatDate(exp.date_of_expense) }}</td>
                <td class="px-3 py-2 whitespace-nowrap">
                  {{ exp.category ? exp.category.name : '—' }}
                </td>
                <td class="px-3 py-2">
                  {{ exp.description }}
                  <div v-if="exp.group_label" class="mt-1">
                    <span
                      class="inline-block px-1.5 py-0.5 text-xs rounded bg-amber-100 text-amber-800"
                      title="Group"
                    >{{ exp.group_label }}</span>
                  </div>
                </td>
                <td class="px-3 py-2 text-right whitespace-nowrap">
                  <span
                    v-if="exp.payment_method === 'company_paid'"
                    class="mr-1 px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-700"
                    title="Paid via company card"
                  >co.</span>
                  ${{ formatAmount(exp.amount) }}
                </td>
                <td class="px-3 py-2">
                  <StatusPill :status="exp.status" />
                  <div v-if="exp.is_retro" class="mt-1">
                    <span class="inline-block px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-500">Retro</span>
                  </div>
                </td>
                <td class="px-3 py-2">
                  <template v-if="exp.is_client_reimbursable">
                    <span>{{ exp.client_name || '—' }}</span>
                    <span v-if="exp.engagement_title"> — {{ exp.engagement_title }}</span>
                  </template>
                  <span v-else class="text-gray-400">not client reimbursable</span>
                </td>
                <td class="px-3 py-2 text-center">{{ exp.receipt_count || 0 }}</td>
              </tr>
              <tr v-if="expandedId === exp.id" class="bg-gray-50 border-t border-gray-200">
                <td></td>
                <td></td>
                <td colspan="9" class="px-3 py-3">
                  <ExpenseReviewPanel
                    :expenseId="exp.id"
                    :detail="detailCache[exp.id]"
                    :loading="detailLoading[exp.id]"
                    :allowApproval="(activeTab === 'pending' || activeTab === 'all_pending') && exp.status === 'submitted'"
                    @approved="onApproved"
                    @rejected="onRejected"
                    @request-reload="reloadDetail(exp.id)"
                  />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </template>

    <BulkRejectModal
      v-if="rejectModalOpen"
      :count="selectedIds.length"
      :submitting="bulkWorking"
      :error="rejectModalError"
      @close="rejectModalOpen = false"
      @confirm="confirmBulkReject"
    />
  </div>
</template>

<script>
export default {
  name: "team-expenses",
};
</script>

<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import {
  getExpenses,
  getExpense,
  getMyTeam,
  bulkManagerApprove,
  bulkManagerReject,
} from "@/services/Expense.js";
import { getExpenseCategories } from "@/services/ExpenseCategory.js";
import StatusPill from "./StatusPill.vue";
import ExpenseReviewPanel from "./ExpenseReviewPanel.vue";
import BulkRejectModal from "./BulkRejectModal.vue";
import { useStickyOffset } from "@/composables/useStickyOffset";

const store = useStore();
const $toast = useToast();

const canViewIsRetro = computed(() => {
  const p = store.state.auth.userPermissions || {};
  return !!(p.isDev || p.isSuperuser || p.isExpenseAdmin || p.isAccountingGroup);
});

const loading = ref(false);
const activeTab = ref("pending");
const topStickyRef = ref(null);
const topStickyHeight = useStickyOffset(topStickyRef);
const team = ref([]);
const categories = ref([]);
const expenses = ref([]);
const allPendingExpenses = ref([]);
const expandedId = ref(null);
const detailCache = reactive({});
const detailLoading = reactive({});

const isApproverOverride = computed(() => {
  const p = store.state.auth.userPermissions || {};
  return !!p.isExpenseApproverOverride;
});

const selectedIds = ref([]);
const bulkWorking = ref(false);
const rejectModalOpen = ref(false);
const rejectModalError = ref("");
const bulkErrors = ref([]);

const filters = reactive({
  search: "",
  submitter: null,
  status: null,
  category: null,
  group_label: "",
  is_retro: null,
  dateFrom: null,
  dateTo: null,
});

const teamGroupLabelOptions = computed(() => {
  const set = new Set();
  const sources = [expenses.value, allPendingExpenses.value];
  for (const list of sources) {
    for (const e of list) {
      if (e.group_label) set.add(e.group_label);
    }
  }
  return Array.from(set).sort();
});

// Submitter dropdown options change with the active tab. Pending My
// Approval is scoped to the user's direct reports, so show only those.
// All Pending shows everyone with a record; History shows the union of
// the team list + anyone who appears in the loaded records.
const submitterOptions = computed(() => {
  if (activeTab.value === 'pending') return team.value;
  const sourceList = activeTab.value === 'all_pending'
    ? allPendingExpenses.value
    : expenses.value;
  const map = new Map();
  if (activeTab.value === 'history') {
    for (const u of team.value) map.set(u.id, u);
  }
  for (const e of sourceList) {
    if (e.submitter && !map.has(e.submitter.id)) {
      map.set(e.submitter.id, e.submitter);
    }
  }
  return Array.from(map.values()).sort((a, b) => {
    const an = (`${a.first_name || ''} ${a.last_name || ''}`.trim() || a.username || '').toLowerCase();
    const bn = (`${b.first_name || ''} ${b.last_name || ''}`.trim() || b.username || '').toLowerCase();
    return an.localeCompare(bn);
  });
});

function passesUniversalFilters(e) {
  if (filters.search) {
    const q = filters.search.trim().toLowerCase();
    if (q) {
      const submitterText = e.submitter
        ? `${e.submitter.first_name || ''} ${e.submitter.last_name || ''} ${e.submitter.username || ''}`
        : '';
      const haystack = [
        e.description,
        e.category ? e.category.name : '',
        e.client_name,
        e.engagement_title,
        submitterText,
        e.group_label,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(q)) return false;
    }
  }
  if (filters.submitter && (!e.submitter || e.submitter.id !== filters.submitter)) return false;
  if (filters.category && (!e.category || e.category.id !== filters.category)) return false;
  if (filters.group_label) {
    const wanted = filters.group_label.trim().toLowerCase();
    if (wanted && (e.group_label || '').toLowerCase() !== wanted) return false;
  }
  if (filters.is_retro !== null && !!e.is_retro !== filters.is_retro) return false;
  if (filters.dateFrom && (!e.date_of_expense || e.date_of_expense < filters.dateFrom)) return false;
  if (filters.dateTo && (!e.date_of_expense || e.date_of_expense > filters.dateTo)) return false;
  return true;
}

const hasActiveFilters = computed(() => !!(
  filters.search ||
  filters.submitter ||
  (activeTab.value === 'history' && filters.status) ||
  filters.category ||
  filters.group_label ||
  filters.is_retro !== null ||
  filters.dateFrom ||
  filters.dateTo
));

const teamIds = computed(() => team.value.map((u) => u.id));

// Tab-scoped sources before universal filters apply.
const pendingBase = computed(() =>
  expenses.value.filter(
    (e) => e.status === "submitted" && teamIds.value.includes(e.submitter && e.submitter.id)
  )
);

const allPendingBase = computed(() => allPendingExpenses.value);

const historyBase = computed(() =>
  expenses.value.filter((e) => teamIds.value.includes(e.submitter && e.submitter.id))
);

// Counts on the tab buttons reflect filtered totals so the UI doesn't
// show "5 pending" while only 1 row is actually visible.
const pendingExpenses = computed(() => pendingBase.value.filter(passesUniversalFilters));
const pendingCount = computed(() => pendingExpenses.value.length);
const allPendingCount = computed(() => allPendingBase.value.filter(passesUniversalFilters).length);

const historyExpenses = computed(() => {
  return historyBase.value.filter((e) => {
    if (!passesUniversalFilters(e)) return false;
    if (filters.status && e.status !== filters.status) return false;
    return true;
  });
});

const visibleExpenses = computed(() => {
  if (activeTab.value === "pending") return pendingExpenses.value;
  if (activeTab.value === "all_pending") return allPendingBase.value.filter(passesUniversalFilters);
  return historyExpenses.value;
});

const filteredTotal = computed(() =>
  visibleExpenses.value.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0)
);

function formatTotal(n) {
  return (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const allPendingSelected = computed(() =>
  visibleExpenses.value.length > 0 &&
  visibleExpenses.value.every((e) => selectedIds.value.includes(e.id))
);

function toggleAllPending() {
  if (allPendingSelected.value) {
    const ids = new Set(visibleExpenses.value.map((e) => e.id));
    selectedIds.value = selectedIds.value.filter((id) => !ids.has(id));
  } else {
    const toAdd = visibleExpenses.value.map((e) => e.id);
    selectedIds.value = Array.from(new Set([...selectedIds.value, ...toAdd]));
  }
}

async function bulkApprove() {
  if (selectedIds.value.length === 0) return;
  bulkWorking.value = true;
  bulkErrors.value = [];
  const token = store.state.auth.token;
  try {
    const result = await bulkManagerApprove(token, [...selectedIds.value]);
    const applied = (result.applied_ids || []).length;
    bulkErrors.value = result.errors || [];
    if (applied > 0) {
      $toast.open({
        message: `Approved ${applied} record${applied === 1 ? '' : 's'}${bulkErrors.value.length ? ' — see errors below' : ''}`,
        type: bulkErrors.value.length ? 'warning' : 'success',
        duration: 2500,
      });
    } else {
      $toast.open({ message: 'No records approved', type: 'error', duration: 2500 });
    }
    selectedIds.value = [];
    await Promise.all([loadExpenses(), loadAllPending()]);
  } catch (error) {
    $toast.open({ message: 'Bulk approve failed', type: 'error', duration: 3000 });
  }
  bulkWorking.value = false;
}

async function confirmBulkReject(reason) {
  if (selectedIds.value.length === 0) return;
  bulkWorking.value = true;
  rejectModalError.value = "";
  bulkErrors.value = [];
  const token = store.state.auth.token;
  try {
    const result = await bulkManagerReject(token, [...selectedIds.value], reason);
    const applied = (result.applied_ids || []).length;
    bulkErrors.value = result.errors || [];
    if (applied > 0) {
      $toast.open({
        message: `Rejected ${applied} record${applied === 1 ? '' : 's'}${bulkErrors.value.length ? ' — see errors below' : ''}`,
        type: bulkErrors.value.length ? 'warning' : 'success',
        duration: 2500,
      });
    } else {
      rejectModalError.value = 'No records rejected.';
    }
    selectedIds.value = [];
    rejectModalOpen.value = false;
    await Promise.all([loadExpenses(), loadAllPending()]);
  } catch (error) {
    rejectModalError.value = 'Bulk reject failed';
  }
  bulkWorking.value = false;
}

function fullName(u) {
  if (!u) return "—";
  const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
  return name || u.username;
}

function formatDate(iso) {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(iso);
  return d.toLocaleDateString();
}

function formatAmount(amount) {
  if (amount === null || amount === undefined) return "0.00";
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  return num.toFixed(2);
}

function clearFilters() {
  filters.submitter = null;
  filters.status = null;
  filters.category = null;
  filters.group_label = "";
  filters.is_retro = null;
  filters.dateFrom = null;
  filters.dateTo = null;
  filters.search = "";
}

async function toggleExpand(id) {
  if (expandedId.value === id) {
    expandedId.value = null;
    return;
  }
  expandedId.value = id;
  if (!detailCache[id]) {
    await loadDetail(id);
  }
}

async function loadDetail(id) {
  detailLoading[id] = true;
  const token = store.state.auth.token;
  try {
    detailCache[id] = await getExpense(token, id);
  } catch (error) {
    $toast.open({ message: "Failed to load expense detail", type: "error", duration: 3000 });
  }
  detailLoading[id] = false;
}

async function reloadDetail(id) {
  await loadDetail(id);
  await loadExpenses();
}

async function loadTeam() {
  const token = store.state.auth.token;
  try {
    team.value = (await getMyTeam(token)) || [];
  } catch (error) {
    console.log(error);
    team.value = [];
  }
}

async function loadCategories() {
  const token = store.state.auth.token;
  try {
    const data = await getExpenseCategories(token);
    categories.value = Array.isArray(data) ? data : (data.results || []);
  } catch (error) {
    console.log(error);
  }
}

async function loadExpenses() {
  loading.value = true;
  const token = store.state.auth.token;
  try {
    // Caller's queryset already includes records where they are the manager.
    // Filter server-side for team records by manager=me.
    const currentUserId = store.state.auth.user ? store.state.auth.user.id : null;
    const qp = currentUserId ? `?manager=${currentUserId}&page_size=500` : "?page_size=500";
    const data = await getExpenses(token, qp);
    expenses.value = Array.isArray(data) ? data : (data.results || []);
  } catch (error) {
    console.log(error);
    $toast.open({ message: "Failed to load team expenses", type: "error", duration: 3000 });
  }
  loading.value = false;
}

async function loadAllPending() {
  if (!isApproverOverride.value) {
    allPendingExpenses.value = [];
    return;
  }
  const token = store.state.auth.token;
  try {
    const data = await getExpenses(token, "?status=submitted&page_size=500");
    allPendingExpenses.value = Array.isArray(data) ? data : (data.results || []);
  } catch (error) {
    console.log(error);
    allPendingExpenses.value = [];
  }
}

function onApproved(updated) {
  detailCache[updated.id] = updated;
  loadExpenses();
}

function onRejected(updated) {
  detailCache[updated.id] = updated;
  loadExpenses();
}

async function initialLoad() {
  await loadTeam();
  await Promise.all([loadCategories(), loadExpenses(), loadAllPending()]);
  // Choose a sensible default tab: a bare override approver (no direct
  // reports) lands on All Pending.
  if (team.value.length === 0 && isApproverOverride.value) {
    activeTab.value = "all_pending";
  }
}

watch(activeTab, () => {
  selectedIds.value = [];
  bulkErrors.value = [];
});

onMounted(initialLoad);
</script>
